import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Heart, Search, Menu, X } from 'lucide-react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import SearchOverlay from './SearchOverlay';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';

// Mega-menu content per category
const MEGA = {
  Men: {
    columns: [
      { title: 'Clothing', links: [
        { to: '/shop/men', label: 'All Men' },
        { to: '/shop/mens-outerwear', label: 'Outerwear' },
        { to: '/shop/mens-knitwear', label: 'Knitwear' },
        { to: '/shop/mens-shirts', label: 'Shirts' },
        { to: '/shop/mens-tees', label: 'Tees' },
        { to: '/shop/mens-trousers', label: 'Trousers' },
        { to: '/shop/mens-denim', label: 'Denim' },
      ]},
      { title: 'By Season', links: [
        { to: '/shop/new-arrivals', label: 'New Arrivals' },
        { to: '/shop/winter', label: 'Winter' },
        { to: '/shop/summer', label: 'Summer' },
        { to: '/shop/essentials', label: 'Essentials' },
        { to: '/shop/sale', label: 'Sale' },
      ]},
    ],
    feature: { image: 'https://images.unsplash.com/photo-1544022613-e87ca75a784a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', label: 'The Overcoat', to: '/shop/mens-outerwear' },
  },
  Women: {
    columns: [
      { title: 'Clothing', links: [
        { to: '/shop/women', label: 'All Women' },
        { to: '/shop/womens-outerwear', label: 'Outerwear' },
        { to: '/shop/womens-knitwear', label: 'Knitwear' },
        { to: '/shop/womens-dresses', label: 'Dresses' },
        { to: '/shop/womens-trousers', label: 'Trousers' },
        { to: '/shop/womens-tees', label: 'Tees' },
        { to: '/shop/womens-skirts', label: 'Skirts' },
      ]},
      { title: 'By Season', links: [
        { to: '/shop/new-arrivals', label: 'New Arrivals' },
        { to: '/shop/winter', label: 'Winter' },
        { to: '/shop/summer', label: 'Summer' },
        { to: '/shop/essentials', label: 'Essentials' },
        { to: '/shop/sale', label: 'Sale' },
      ]},
    ],
    feature: { image: 'https://images.unsplash.com/photo-1548624313-0396c75e4b1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', label: 'The Wrap Coat', to: '/shop/womens-outerwear' },
  },
  Kids: {
    columns: [
      { title: 'Clothing', links: [
        { to: '/shop/kids', label: 'All Kids' },
        { to: '/shop/kids-outerwear', label: 'Outerwear' },
        { to: '/shop/kids-knitwear', label: 'Knitwear' },
        { to: '/shop/kids-tees', label: 'Tees' },
        { to: '/shop/kids-bottoms', label: 'Bottoms' },
      ]},
      { title: 'By Season', links: [
        { to: '/shop/new-arrivals', label: 'New Arrivals' },
        { to: '/shop/winter', label: 'Winter' },
        { to: '/shop/summer', label: 'Summer' },
      ]},
    ],
    feature: { image: 'https://images.unsplash.com/photo-1519554089793-9d6d3ba55c65?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', label: 'The Terry Hoodie', to: '/shop/kids' },
  },
  Accessories: {
    columns: [
      { title: 'Small Goods', links: [
        { to: '/shop/accessories', label: 'All Accessories' },
        { to: '/shop/bags', label: 'Bags' },
        { to: '/shop/shoes', label: 'Shoes' },
        { to: '/shop/hats-scarves', label: 'Hats & Scarves' },
        { to: '/shop/jewellery', label: 'Jewellery' },
      ]},
      { title: 'Curated', links: [
        { to: '/shop/new-arrivals', label: 'New Arrivals' },
        { to: '/shop/essentials', label: 'Essentials' },
        { to: '/shop/sale', label: 'Sale' },
      ]},
    ],
    feature: { image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', label: 'The Crossbody', to: '/shop/bags' },
  },
};

const PRIMARY = ['Men', 'Women', 'Kids', 'Accessories'];
const SECONDARY = [
  { to: '/shop/new-arrivals', label: 'New Arrivals' },
  { to: '/shop/sale', label: 'Sale' },
  { to: '/lookbook', label: 'Lookbook' },
  { to: '/blog', label: 'Journal' },
  { to: '/about', label: 'About' },
];

export default function Header() {
  const { count, setIsOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeTimer = useRef(null);
  const mobileCloseRef = useRef(null);
  const mobileTriggerRef = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenMega(null); setSearchOpen(false); }, [loc.pathname]);

  // Mobile menu focus management: move focus in on open, trap Tab, restore on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.activeElement;
    mobileTriggerRef.current = prev;
    document.body.style.overflow = 'hidden';
    setTimeout(() => mobileCloseRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === 'Escape') { setMobileOpen(false); return; }
      if (e.key !== 'Tab') return;
      const container = document.querySelector('[data-testid="mobile-nav"]');
      if (!container) return;
      // Exclude the branding logo from the focus loop so the trap wraps
      // between the top-row utilities (Search/Close) and the nav links.
      const all = Array.from(container.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
      const focusables = all.filter((el) => el.getAttribute('data-testid') !== 'site-logo');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [mobileOpen]);

  const openMenu = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMega(label);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMega(null), 140);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 w-full transition-[background-color,backdrop-filter,border-color] duration-500 ease-cinema ${
          scrolled || openMega
            ? 'bg-background/80 backdrop-blur-xl border-b border-border'
            : 'bg-background/40 backdrop-blur-md border-b border-transparent'
        }`}
        onMouseLeave={scheduleClose}
      >
        {/* Top strip */}
        <div className="hidden md:block border-b border-border/60">
          <div className="container flex h-8 items-center justify-between overline text-muted-foreground">
            <span>Complimentary shipping over $150</span>
            <span>A Legacy in Every Mark</span>
            <span>Est. 2026</span>
          </div>
        </div>

        <div className="container flex h-16 md:h-20 items-center justify-between gap-8">
          <div className="flex items-center gap-8">
            <button
              type="button"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Logo />
          </div>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-10">
            {PRIMARY.map((label) => (
              <div key={label} onMouseEnter={() => openMenu(label)} onFocus={() => openMenu(label)} className="relative">
                <NavLink
                  to={`/shop/${label.toLowerCase()}`}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `overline link-underline py-6 inline-block transition-opacity duration-500 ${isActive || openMega === label ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`
                  }
                >
                  {label}
                </NavLink>
              </div>
            ))}
            {SECONDARY.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `overline link-underline transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Search"
              data-testid="nav-search"
              onClick={() => setSearchOpen(true)}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center hover:bg-foreground/5 transition-colors duration-500"
            >
              <Search className="h-4 w-4" />
            </button>
            <ThemeToggle />
            <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist" className="relative inline-flex h-10 w-10 items-center justify-center hover:bg-foreground/5 transition-colors duration-500">
              <Heart className="h-4 w-4" />
              {wishCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] font-medium bg-foreground text-background inline-flex items-center justify-center tabular">
                  {wishCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {openMega && MEGA[openMega] && (
            <motion.div
              key={openMega}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.85, 0, 0.15, 1] }}
              className="absolute left-0 right-0 top-full bg-background/95 backdrop-blur-xl border-b border-border shadow-[0_20px_30px_-20px_rgba(0,0,0,0.15)]"
              onMouseEnter={() => openMenu(openMega)}
              onMouseLeave={scheduleClose}
              data-testid={`mega-menu-${openMega.toLowerCase()}`}
            >
              <div className="container py-12 grid grid-cols-12 gap-10">
                {MEGA[openMega].columns.map((col) => (
                  <div key={col.title} className="col-span-3">
                    <p className="overline text-muted-foreground mb-5">{col.title}</p>
                    <ul className="space-y-3">
                      {col.links.map((l) => (
                        <li key={l.label}>
                          <Link to={l.to} className="text-sm link-underline">{l.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="col-span-6">
                  <Link to={MEGA[openMega].feature.to} className="group block kb-parent">
                    <div className="aspect-[16/9] overflow-hidden bg-muted">
                      <img src={MEGA[openMega].feature.image} alt={MEGA[openMega].feature.label} className="kb-img h-full w-full object-cover" />
                    </div>
                    <div className="mt-4 flex items-baseline justify-between">
                      <p className="overline text-muted-foreground">Featured</p>
                      <p className="font-display text-2xl">{MEGA[openMega].feature.label}</p>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile drawer — animated, portaled for correct stacking + focus mgmt */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              key="mobile-nav"
              className="lg:hidden fixed inset-0 z-[100] bg-background flex flex-col"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
              data-testid="mobile-nav"
            >
              <div className="container flex h-16 items-center justify-between border-b border-border">
                <Logo />
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
                    aria-label="Open search"
                    className="h-10 w-10 inline-flex items-center justify-center hover:bg-foreground/5"
                    data-testid="mobile-nav-search"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                  <button ref={mobileCloseRef} aria-label="Close menu" onClick={() => setMobileOpen(false)} className="h-10 w-10 inline-flex items-center justify-center" data-testid="mobile-menu-close">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <nav className="container mt-8 flex-1 overflow-y-auto pb-12">
                {[...PRIMARY.map((p) => ({ to: `/shop/${p.toLowerCase()}`, label: p })), ...SECONDARY,
                  { to: '/contact', label: 'Contact' }, { to: '/faq', label: 'FAQ' }
                ].map((n, i) => (
                  <motion.div
                    key={n.to + n.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: [0.85, 0, 0.15, 1] }}
                    className="border-b border-border"
                  >
                    <Link to={n.to} className="font-display text-4xl tracking-tight py-5 block">{n.label}</Link>
                  </motion.div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
